/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 选择需要检测的时间组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-03-03 19:24:37
 */

import React, { useState, useRef } from 'react'
import { DatePicker, InputNumber, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'react-redux'
import useAITaskEvent, {
  PHOTO_SIMILAR_TASK_NAME
} from '../hooks/useAITaskEvent'
import { useSensorTracking } from '../hooks/useSensorTracking'
import './SelectDetectionTime.scss'
import {
  ArrowRightOutlined,
  BulbOutlined,
  CalendarOutlined,
  ClusterOutlined,
  SafetyCertificateOutlined,
  SettingOutlined
} from '@ant-design/icons'
// antd 使用 Dayjs 类型替代 RangePickerValue
type RangePickerValue = [Dayjs | null, Dayjs | null] | null

const { RangePicker } = DatePicker

interface SelectDetectionTimeProps {
  dateRange: RangePickerValue
  onDateRangeChange: (
    dates: RangePickerValue,
    dateStrings: [string, string]
  ) => void
  onStartDetection: (config?: {
    groupsPerPage: number
    photosPerGroup: number
    smartDetection: boolean
    sortByTime?: boolean
  }) => void
  onEnterPreparing: () => void
}

const SelectDetectionTime: React.FC<SelectDetectionTimeProps> = ({
  dateRange,
  onDateRangeChange,
  onStartDetection,
  onEnterPreparing
}) => {
  const smartDetection = true
  const [dates, setDates] = useState<any>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<any>(null)
  const [groupsPerPage, setGroupsPerPage] = useState<number>(10) // 每页显示的相似组数量，默认10
  const [photosPerGroup, setPhotosPerGroup] = useState<number>(12)
  const sortByTime = true // 是否按照时间排序，默认勾选
  const [checkingPermission, setCheckingPermission] = useState(false)
  const [enabling, setEnabling] = useState(false)

  // 获取Redux中的groupId
  const groupId = useSelector((state: any) => state.group.groupId)

  // 本地记录该团队是否已提交过开启请求，用于展示准备中状态
  const appliedStorageKey = groupId ? `photo_similar_applied_${groupId}` : ''
  const hasApplied =
    !!appliedStorageKey && !!localStorage.getItem(appliedStorageKey)

  // 引入AI任务Hook
  const { syncPhotoSimilarFuncStatus, syncTask } = useAITaskEvent()

  // 引入埋点Hook
  const { trackComplete } = useSensorTracking()

  const isDateRangeValid =
    dateRange && Array.isArray(dateRange) && dateRange[0] && dateRange[1]

  const disabledDate = (current: dayjs.Dayjs | null) => {
    if (!current) {
      return false
    }
    const isFuture = current > dayjs().endOf('day')
    if (isFuture) {
      return true
    }
    if (dates && dates.length > 0 && dates[0]) {
      const diff = current.diff(dates[0], 'years', true)
      return Math.abs(diff) > 1
    }
    return false
  }

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDates([])
    }
  }

  const handlePresetChange = (
    type: 'last30' | 'last3Months' | 'last6Months' | 'last1Year'
  ) => {
    let start, end
    const today = dayjs().endOf('day')

    switch (type) {
      case 'last30':
        start = dayjs().subtract(29, 'days').startOf('day')
        end = today
        break
      case 'last3Months':
        start = dayjs().subtract(3, 'months').startOf('day')
        end = today
        break
      case 'last6Months':
        start = dayjs().subtract(6, 'months').startOf('day')
        end = today
        break
      case 'last1Year':
        start = dayjs().subtract(1, 'year').startOf('day')
        end = today
        break
    }

    if (start && end) {
      onDateRangeChange(
        [start, end],
        [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
      )
    }
  }

  // 判断当前选择的日期范围是否匹配预设
  const getActivePreset = ():
    | 'last30'
    | 'last3Months'
    | 'last6Months'
    | 'last1Year'
    | null => {
    if (!isDateRangeValid) return null

    const [start, end] = dateRange as [Dayjs, Dayjs]
    const today = dayjs().endOf('day')

    // 过去30天
    if (
      start.isSame(dayjs().subtract(29, 'days').startOf('day'), 'day') &&
      end.isSame(today, 'day')
    ) {
      return 'last30'
    }

    // 过去3个月
    if (
      start.isSame(dayjs().subtract(3, 'months').startOf('day'), 'day') &&
      end.isSame(today, 'day')
    ) {
      return 'last3Months'
    }

    // 过去6个月
    if (
      start.isSame(dayjs().subtract(6, 'months').startOf('day'), 'day') &&
      end.isSame(today, 'day')
    ) {
      return 'last6Months'
    }

    // 过去一年
    if (
      start.isSame(dayjs().subtract(1, 'year').startOf('day'), 'day') &&
      end.isSame(today, 'day')
    ) {
      return 'last1Year'
    }

    return null
  }

  const activePreset = getActivePreset()

  // 格式化日期显示为中文格式
  const formatDateDisplay = () => {
    if (!isDateRangeValid) {
      return '请选择日期范围'
    }

    const [start, end] = dateRange as [Dayjs, Dayjs]
    const today = dayjs().endOf('day')

    // 如果结束日期是今天，显示"至今"
    if (end && end.isSame(today, 'day')) {
      return `${start.format('YYYY年M月D日')} - 至今`
    }

    if (end) {
      return `${start.format('YYYY年M月D日')} - ${end.format('YYYY年M月D日')}`
    }

    return start.format('YYYY年M月D日')
  }

  // 检查用户权限
  const checkPermission = async () => {
    if (!groupId) {
      message.error('未获取到团队ID，请刷新重试')
      return false
    }

    setCheckingPermission(true)
    try {
      const res = await syncPhotoSimilarFuncStatus({
        group_id: String(groupId)
      })

      if (res.code === 200 && res.output) {
        const hasFunc = res.output.has_func
        if (hasFunc && appliedStorageKey) {
          localStorage.removeItem(appliedStorageKey)
        }
        setCheckingPermission(false)
        return hasFunc
      } else {
        setCheckingPermission(false)
        message.error(res.msg || '权限检查失败，请稍后重试')
        return false
      }
    } catch (error) {
      console.error('权限检查失败:', error)
      setCheckingPermission(false)
      message.error('权限检查过程发生错误，请稍后重试')
      return false
    }
  }

  // 处理开始检测按钮点击
  const handleStartClick = async () => {
    if (!isDateRangeValid) {
      return
    }

    // 先检查权限
    const hasPermission = await checkPermission()
    if (!hasPermission) {
      if (hasApplied) {
        onEnterPreparing()
        return
      }

      if (!groupId) {
        message.error('未获取到团队ID，请刷新重试')
        return
      }

      setEnabling(true)
      try {
        const res = await syncTask({
          taskName: PHOTO_SIMILAR_TASK_NAME,
          taskRoute: 'func_status',
          inputParam: { group_id: String(groupId), add: 1 }
        })

        if (res?.code === 200) {
          if (appliedStorageKey) {
            localStorage.setItem(appliedStorageKey, '1')
          }
          onEnterPreparing()
        } else {
          message.error(res?.msg || '开启失败，请稍后重试')
        }
      } catch (error) {
        console.error('开启 AI 深度智能扫描失败:', error)
        message.error('开启失败，请稍后重试')
      } finally {
        setEnabling(false)
      }
      return
    }

    // 获取配置值（state 已有默认值，直接使用）
    const finalGroupsPerPage = groupsPerPage
    const finalPhotosPerGroup = photosPerGroup
    const finalSortByTime = sortByTime

    // 获取日期范围
    const startDate = dateRange[0]?.format('YYYY-MM-DD') || ''
    const endDate = dateRange[1]?.format('YYYY-MM-DD') || ''

    // 上报完成检测埋点
    trackComplete({
      sortByTime: finalSortByTime,
      startDate,
      endDate,
      groupsPerPage: finalGroupsPerPage,
      photosPerGroup: finalPhotosPerGroup
    })

    // 有权限，继续执行检测
    onStartDetection({
      groupsPerPage: finalGroupsPerPage,
      photosPerGroup: finalPhotosPerGroup,
      smartDetection,
      sortByTime: finalSortByTime
    })
  }

  return (
    <div className="select-detection-time">
      <div className="background-effects">
        <div className="effect-blob blob-blue"></div>
        <div className="effect-blob blob-indigo"></div>
        <div className="center-glow"></div>
      </div>

      <div className="content-container">
        {/* Left Side: Intro & Features */}
        <div className="left-panel">
          <div className="intro-content">
            <h1 className="main-title">
              重复照片
              <br />
              <span className="gradient-text">一键智能去重</span>
            </h1>
          </div>
          <div className="features-list">
            <div className="feature-item group">
              <div className="feature-icon-wrapper">
                <SettingOutlined className="feature-icon" />
              </div>
              <div className="feature-text">
                <h3 className="feature-title">释放空间，节省成本</h3>
              </div>
            </div>
            <div className="feature-item group">
              <div className="feature-icon-wrapper">
                <ClusterOutlined className="feature-icon" />
              </div>
              <div className="feature-text">
                <h3 className="feature-title">分组决策，一键保留最佳</h3>
              </div>
            </div>
            <div className="feature-item group">
              <div className="feature-icon-wrapper">
                <SafetyCertificateOutlined className="feature-icon" />
              </div>
              <div className="feature-text">
                <h3 className="feature-title">智能分组，省时省力</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Configuration Cards */}
        <div className="right-panel">
          <div className="config-container">
            <div className="section-header">
              <h2 className="section-title">配置扫描参数</h2>
            </div>

            {/* Time Selection Card */}
            <div className="config-card time-card group">
              <div className="card-header">
                <label className="card-label">时间范围</label>
                <CalendarOutlined className="card-icon" />
              </div>
              <div className="card-content">
                <div
                  className="range-picker-wrapper"
                  onClick={() => {
                    if (!pickerOpen) {
                      setPickerOpen(true)
                    }
                  }}
                >
                  <div className="date-display">{formatDateDisplay()}</div>
                  <RangePicker
                    ref={pickerRef}
                    value={dateRange}
                    onChange={onDateRangeChange}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    disabledDate={disabledDate}
                    onCalendarChange={(val) => setDates(val)}
                    onOpenChange={(open) => {
                      setPickerOpen(open)
                      handleOpenChange(open)
                    }}
                    open={pickerOpen}
                    separator="-"
                    suffixIcon={null}
                    className="custom-range-picker"
                    placeholder={['开始日期', '结束日期']}
                  />
                </div>
                <div className="date-divider"></div>
                <div className="quick-actions">
                  <button
                    className={`quick-btn ${
                      activePreset === 'last30' ? 'active' : ''
                    }`}
                    onClick={() => handlePresetChange('last30')}
                  >
                    过去30天
                  </button>
                  <button
                    className={`quick-btn ${
                      activePreset === 'last3Months' ? 'active' : ''
                    }`}
                    onClick={() => handlePresetChange('last3Months')}
                  >
                    过去3个月
                  </button>
                  <button
                    className={`quick-btn ${
                      activePreset === 'last6Months' ? 'active' : ''
                    }`}
                    onClick={() => handlePresetChange('last6Months')}
                  >
                    过去6个月
                  </button>
                  <button
                    className={`quick-btn ${
                      activePreset === 'last1Year' ? 'active' : ''
                    }`}
                    onClick={() => handlePresetChange('last1Year')}
                  >
                    过去一年
                  </button>
                </div>
              </div>
            </div>

            {/* Smart Detection Card */}
            <div className="config-card smart-card group">
              <div className="smart-content">
                <div className="smart-info">
                  <div className="smart-icon-box">
                    <BulbOutlined className="smart-icon" />
                  </div>
                  <div className="smart-text">
                    <h4 className="smart-title">结果预览分页配置</h4>
                    <p className="smart-desc">
                      配置搜索结果预览参数，让结果预览更省心。
                    </p>
                  </div>
                </div>
              </div>
              {smartDetection && (
                <div className="smart-config">
                  <div className="config-item">
                    <div className="config-label-wrap">
                      <label className="config-label">
                        每个相似组的照片数量（横向分页）
                      </label>
                      <span className="config-hint">每行展示6个</span>
                    </div>
                    <InputNumber
                      min={6}
                      max={96}
                      step={1}
                      value={photosPerGroup}
                      onChange={(value) => {
                        const v = value ?? 12
                        const num = Math.max(6, Math.min(96, Math.round(v)))
                        setPhotosPerGroup(num)
                      }}
                      className="config-input"
                    />
                  </div>
                  <div className="config-item">
                    <div className="config-label-wrap">
                      <label className="config-label">
                        每页显示的相似组数量（竖向分页）
                      </label>
                    </div>
                    <InputNumber
                      min={1}
                      max={100}
                      step={1}
                      value={groupsPerPage}
                      onChange={(value) => {
                        const v = value ?? 10
                        const num = Math.max(1, Math.min(100, Math.round(v)))
                        setGroupsPerPage(num)
                      }}
                      className="config-input"
                    />
                  </div>
                  {/* <div className="config-item config-item-switch">
                    <label className="config-label">是否按照时间排序</label>
                    <Switch
                      checked={sortByTime}
                      onChange={setSortByTime}
                      className="config-switch"
                    />
                  </div> */}
                </div>
              )}
            </div>

            {/* Start Button */}
            <div className="action-area">
              <button
                className="start-btn-large group"
                onClick={handleStartClick}
                disabled={!isDateRangeValid || checkingPermission || enabling}
              >
                <div className="btn-bg"></div>
                <div className="btn-content">
                  <span className="btn-text">
                    {checkingPermission
                      ? '检查权限中...'
                      : enabling
                        ? '开启中...'
                        : '开启 AI 深度智能扫描'}
                  </span>
                  {!checkingPermission && !enabling && (
                    <ArrowRightOutlined className="btn-icon" />
                  )}
                </div>
                <div className="btn-shimmer"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectDetectionTime
